<template>
  <div class="panel">
    <!-- selection context opens the rail -->
    <template v-if="items.length">
      <h6 class="text-muted heading">Your {{ itemNoun }}{{ items.length > 1 ? 's' : '' }}</h6>
      <hr class="hr" />
      <div v-for="item in items" :key="item.uid" class="mini-card" :class="{ clickable: miniText(item) }" @click="toggleCard(item.uid)">
        <img v-if="miniImage(item)" :src="miniImage(item)" :class="['thumb', mode === 'movies' ? 'square' : 'circle']" alt="" />
        <span v-else :class="['thumb', 'thumb-blank', mode === 'movies' ? 'square' : 'circle']">{{ initials(item.label) }}</span>
        <span class="mini-text">
          <span class="mini-title">{{ item.label }}<span v-if="miniSub(item)" class="text-muted mini-sub"> · {{ miniSub(item) }}</span></span>
          <span v-if="miniText(item)" class="text-muted mini-plot" :class="{ open: expandedIds.includes(item.uid) }">{{ miniText(item) }}</span>
        </span>
      </div>
    </template>

    <!-- answers swap with a slide: the leaving section exits right, its
         replacement enters from the left -->
    <Transition name="swap" mode="out-in">
      <!-- a selection is on the chart but its credits are still loading -->
      <div v-if="anyLoading" key="loading" class="swap-block">
        <h6 class="text-muted heading">{{ loadingHeading }}</h6>
        <hr class="hr" />
        <p class="text-muted note">Pulling {{ mode === 'movies' ? 'the cast list' : 'the credits' }} from TMDB…</p>
      </div>

      <!-- single selection: best next moves -->
      <div v-else-if="items.length === 1" key="moves" class="swap-block">
        <h6 class="text-muted heading">Best next moves</h6>
        <hr class="hr" />
        <template v-if="movesLoading">
          <div v-for="n in 3" :key="'move-skel-' + n" class="row-card">
            <span :class="['skel', 'skel-avatar', mode === 'movies' ? 'square' : 'circle']"></span>
            <span class="row-text">
              <span class="skel skel-line" :style="{ width: (72 - n * 10) + '%' }"></span>
              <span class="skel skel-line thin" :style="{ width: (88 - n * 6) + '%' }"></span>
            </span>
          </div>
        </template>
        <template v-else-if="moves.length">
          <div v-for="move in moves" :key="move.entity.uid" class="row-card clickable" @click="$emit('add', move.entity)">
            <img v-if="moveImage(move.entity)" :src="moveImage(move.entity)" :class="['avatar', mode === 'movies' ? 'square' : 'circle']" alt="" />
            <span v-else :class="['monogram', mode === 'movies' ? 'square' : 'circle']">{{ initials(move.entity.title || move.entity.name) }}</span>
            <span class="row-text">
              <span class="row-title">+ {{ move.entity.title || move.entity.name }}</span>
              <span class="row-sub text-muted">keeps {{ keepsLabel(move) }}</span>
            </span>
          </div>
        </template>
        <p v-else class="text-muted note">No strong next moves found — add any other {{ itemNoun }} to intersect.</p>
      </div>

      <!-- multiple selections with matches -->
      <div v-else-if="shared.length" key="matches" class="swap-block">
        <h6 class="text-muted heading">{{ matchHeading }} · {{ shared.length }}</h6>
        <hr class="hr" />
        <div class="scroll-list">
          <div v-for="(credit, index) in shared" :key="credit.uid" class="row-card match clickable" @click="$emit('pivot', credit)">
            <img v-if="creditImage(credit)" :src="creditImage(credit)" :class="['avatar', mode === 'movies' ? 'circle' : 'square']" alt="" />
            <span v-else :class="['monogram', mode === 'movies' ? 'circle' : 'square', { dim: index > 0 }]">{{ initials(credit.name || credit.title) }}</span>
            <span class="row-text">
              <span class="row-title big">{{ credit.name || credit.title }}</span>
              <span v-if="credit.roles && credit.roles.length" class="row-sub roles">{{ credit.roles.join(' · ') }}</span>
            </span>
          </div>
        </div>
        <p class="text-muted hint">Click {{ mode === 'movies' ? 'an actor' : 'a title' }} to pivot · add another {{ itemNoun }} to narrow</p>
      </div>

      <!-- multiple selections, no match -->
      <div v-else key="none" class="swap-block">
        <h6 class="text-muted heading">No shared {{ sharedNoun }}</h6>
        <hr class="hr" />
        <template v-if="rescue">
          <p class="text-muted note">{{ rescue.item.label }} shares no {{ sharedNoun }} with the other{{ items.length > 2 ? 's' : '' }}. Without it, you'd have:</p>
          <div v-for="credit in rescue.kept" :key="credit.uid" class="row-card">
            <img v-if="creditImage(credit)" :src="creditImage(credit)" :class="['avatar', mode === 'movies' ? 'circle' : 'square']" alt="" />
            <span v-else class="monogram" :class="mode === 'movies' ? 'circle' : 'square'">{{ initials(credit.name || credit.title) }}</span>
            <span class="row-text">
              <span class="row-title">{{ credit.name || credit.title }}</span>
            </span>
          </div>
          <button class="btn btn-secondary remove-btn" @click="$emit('remove', rescue.item.uid)">× Remove {{ rescue.item.label }}</button>
        </template>
        <template v-else>
          <p class="text-muted note">These {{ items.length === 2 ? 'two' : items.length }} {{ castsNoun }} never overlap. Try removing one:</p>
          <button v-for="item in items" :key="item.uid" class="btn btn-secondary remove-btn" @click="$emit('remove', item.uid)">× Remove {{ item.label }}</button>
        </template>
      </div>
    </Transition>

    <!-- TMDB's taste closes the rail: context (your title) → act (moves) → wander -->
    <template v-if="showShelf">
      <div class="shelf-head">
        <h6 class="text-muted heading">Similar</h6>
        <span class="text-muted shelf-src">via TMDB<template v-if="recs.length"> · {{ recs.length }}</template></span>
      </div>
      <hr class="hr" />
      <div v-if="recsLoading" class="shelf">
        <div class="shelf-strip">
          <div v-for="n in 4" :key="'rec-skel-' + n" class="shelf-tile">
            <span class="skel skel-poster"></span>
            <span class="skel skel-line thin centered" :style="{ width: (78 - n * 9) + '%' }"></span>
          </div>
        </div>
      </div>
      <template v-else>
        <div class="shelf">
          <div class="shelf-strip" ref="shelfStrip" @scroll.passive="updateShelfEdges">
            <div
              v-for="rec in recs"
              :key="rec.uid"
              class="shelf-tile"
              :title="shelfTooltip(rec)"
              @click="$emit('add', rec)"
            >
              <span class="shelf-poster-wrap">
                <img class="shelf-poster" :src="`https://image.tmdb.org/t/p/w154${rec.poster_path}`" :alt="rec.title || rec.name" loading="lazy" />
                <span v-if="rec.shared.length" class="shelf-badge">{{ rec.shared.length }}</span>
              </span>
              <span class="shelf-label text-muted">{{ rec.title || rec.name }}</span>
              <span v-if="yearOf(rec)" class="shelf-year text-muted">{{ yearOf(rec) }}</span>
            </div>
          </div>
          <div :class="['shelf-fade', 'left', { hidden: shelfAtStart }]"></div>
          <div :class="['shelf-fade', 'right', { hidden: shelfAtEnd }]"></div>
          <button v-if="!shelfAtStart" class="shelf-nav left" aria-label="Scroll left" @click="nudgeShelf(-1)">‹</button>
          <button v-if="!shelfAtEnd" class="shelf-nav right" aria-label="Scroll right" @click="nudgeShelf(1)">›</button>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
  import { yearOf } from '../helper/tmdb';

  export default {
    name: 'ResultsPanel',
    props: {
      items: {
        type: Array,
        required: true
      },
      shared: {
        type: Array,
        required: true
      },
      mode: {
        type: String,
        required: true
      },
      moves: {
        type: Array,
        required: true
      },
      movesLoading: {
        type: Boolean,
        default: false
      },
      rescue: {
        type: Object,
        default: null
      },
      recs: {
        type: Array,
        default: () => []
      },
      recsLoading: {
        type: Boolean,
        default: false
      }
    },
    emits: ['add', 'pivot', 'remove'],
    data() {
      return {
        expandedIds: [],
        shelfAtStart: true,
        shelfAtEnd: true
      };
    },
    watch: {
      // the strip only exists once the recs are in the DOM, so re-measure after render
      recs() {
        this.$nextTick(this.updateShelfEdges);
      },
      recsLoading() {
        this.$nextTick(this.updateShelfEdges);
      }
    },
    mounted() {
      window.addEventListener('resize', this.updateShelfEdges);
    },
    beforeUnmount() {
      window.removeEventListener('resize', this.updateShelfEdges);
    },
    computed: {
      anyLoading() {
        return this.items.some(item => item.loading);
      },
      loadingHeading() {
        const item = this.items.find(entry => entry.loading);
        return item ? `Adding ${item.label}` : 'Loading';
      },
      itemNoun() {
        return this.mode === 'movies' ? 'title' : 'actor';
      },
      sharedNoun() {
        return this.mode === 'movies' ? 'actors' : 'credits';
      },
      castsNoun() {
        return this.mode === 'movies' ? 'casts' : 'filmographies';
      },
      showShelf() {
        return this.mode === 'movies'
          && this.items.length === 1
          && !this.anyLoading
          && (this.recsLoading || this.recs.length > 0);
      },
      matchHeading() {
        const count = this.items.length;
        if (this.mode === 'movies') {
          return count === 2 ? 'In both titles' : `In all ${count} titles`;
        }
        return count === 2 ? 'In both filmographies' : `In all ${count} filmographies`;
      }
    },
    methods: {
      miniSub(item) {
        if (this.mode === 'movies') {
          return [item.year, item.genre].filter(Boolean).join(' · ');
        }
        return [
          item.born && `b. ${new Date(item.born).getFullYear()}`,
          item.credits.length && `${item.credits.length} credits`
        ].filter(Boolean).join(' · ');
      },
      miniText(item) {
        return (this.mode === 'movies' ? item.overview : item.bio) || '';
      },
      miniImage(item) {
        const path = this.mode === 'movies' ? item.poster : item.profile;
        return path ? `https://image.tmdb.org/t/p/w92${path}` : '';
      },
      toggleCard(uid) {
        this.expandedIds = this.expandedIds.includes(uid)
          ? this.expandedIds.filter(id => id !== uid)
          : [...this.expandedIds, uid];
      },
      initials(name) {
        if (!name) return '?';
        return name.split(' ').filter(Boolean).slice(0, 2).map(word => word[0].toUpperCase()).join('');
      },
      creditImage(credit) {
        const path = this.mode === 'movies' ? credit.profile_path : credit.poster_path;
        return path ? `https://image.tmdb.org/t/p/w185${path}` : '';
      },
      moveImage(entity) {
        const path = this.mode === 'movies' ? entity.poster_path : entity.profile_path;
        return path ? `https://image.tmdb.org/t/p/w185${path}` : '';
      },
      // a few px of slack so subpixel scroll positions still count as the edge
      updateShelfEdges() {
        const strip = this.$refs.shelfStrip;
        if (!strip) return;
        this.shelfAtStart = strip.scrollLeft <= 4;
        this.shelfAtEnd = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 4;
      },
      // page by almost a full viewport, leaving one tile of overlap for context
      nudgeShelf(direction) {
        const strip = this.$refs.shelfStrip;
        if (!strip) return;
        strip.scrollBy({ left: direction * (strip.clientWidth - 72), behavior: 'smooth' });
      },
      yearOf,
      shelfTooltip(rec) {
        const year = yearOf(rec);
        const name = (rec.title || rec.name) + (year ? ` (${year})` : '');
        return rec.shared.length
          ? `${name} — shares ${rec.shared.slice(0, 3).join(', ')}${rec.shared.length > 3 ? '…' : ''}`
          : name;
      },
      keepsLabel(move) {
        const names = move.keeps.slice(0, 2).join(' + ');
        const extra = move.keeps.length - 2;
        return extra > 0 ? `${names} +${extra}` : names;
      }
    }
  }
</script>

<style scoped>
  .panel {
    flex: 0 0 270px;
    display: flex;
    flex-direction: column;
    gap: 11px;
    min-width: 0;
    padding-top: 14px;
    animation: rail-in 0.5s ease-out 0.85s backwards;
  }
  @keyframes rail-in {
    from {
      opacity: 0;
      transform: translateX(-24px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .panel {
      animation: none;
    }
  }
  .heading {
    margin: 0;
  }
  .note {
    font-size: 12.5px;
    margin: 0;
    line-height: 1.5;
  }
  .mini-card {
    display: flex;
    gap: 11px;
    padding: 4px 0;
  }
  .mini-card.clickable {
    cursor: pointer;
  }
  .thumb {
    width: 40px;
    flex: none;
    object-fit: cover;
  }
  .thumb.square {
    height: 60px;
    border-radius: 4px;
  }
  .thumb.circle {
    height: 40px;
    border-radius: 50%;
  }
  .thumb-blank {
    display: grid;
    place-items: center;
    background: var(--color-accent-900);
    color: var(--color-accent-200);
    font-size: 12px;
    font-weight: 500;
  }
  .thumb-blank.circle {
    background: var(--color-accent-800);
    color: var(--color-accent-100);
  }
  .mini-text {
    min-width: 0;
    display: block;
  }
  .mini-title {
    display: block;
    font-size: 13px;
    margin-bottom: 2px;
  }
  .mini-sub {
    font-size: 11px;
  }
  /* clamped to two lines per 23a — clicking the card expands the full plot */
  .mini-plot {
    font-size: 11.5px;
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .mini-plot.open {
    -webkit-line-clamp: unset;
  }
  .swap-block {
    display: flex;
    flex-direction: column;
    gap: 11px;
    min-width: 0;
    margin-top: 8px;
  }
  /* the answers section leaves to the right, its replacement arrives from the left */
  .swap-enter-active,
  .swap-leave-active {
    transition: transform 0.25s ease, opacity 0.25s ease;
  }
  .swap-enter-from {
    transform: translateX(-24px);
    opacity: 0;
  }
  .swap-leave-to {
    transform: translateX(24px);
    opacity: 0;
  }
  @media (prefers-reduced-motion: reduce) {
    .swap-enter-active,
    .swap-leave-active {
      transition: none;
    }
  }
  .row-card {
    display: flex;
    align-items: center;
    gap: 11px;
    background: var(--color-surface);
    border-radius: 8px;
    padding: 8px 11px;
    flex: none;
  }
  .row-card.match {
    padding: 11px;
    box-shadow: inset 0 0 0 1px var(--color-accent-700);
  }
  .row-card.clickable {
    cursor: pointer;
  }
  .row-card.clickable:hover {
    background: color-mix(in srgb, var(--color-accent) 12%, var(--color-surface));
  }
  .monogram {
    width: 36px;
    height: 36px;
    flex: none;
    display: grid;
    place-items: center;
    font-size: 12px;
    font-weight: 500;
    background: var(--color-accent-800);
    color: var(--color-accent-100);
  }
  .monogram.dim {
    background: var(--color-neutral-800);
    color: var(--color-neutral-200);
  }
  .monogram.circle {
    border-radius: 50%;
  }
  .monogram.square {
    border-radius: 8px;
    background: var(--color-accent-900);
    color: var(--color-accent-200);
  }
  .avatar {
    width: 36px;
    height: 36px;
    flex: none;
    object-fit: cover;
  }
  .avatar.circle {
    border-radius: 50%;
  }
  .avatar.square {
    border-radius: 8px;
  }
  .row-text {
    min-width: 0;
    display: block;
  }
  .row-title {
    display: block;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .row-title.big {
    font-size: 13.5px;
  }
  .row-sub {
    display: block;
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .row-sub.roles {
    color: var(--color-accent-300);
  }
  .scroll-list {
    display: flex;
    flex-direction: column;
    gap: 11px;
  }
  .hint {
    font-size: 11px;
    margin: 0;
  }
  /* skeletons mirror the shapes they stand in for */
  .skel {
    display: block;
    background: color-mix(in srgb, var(--color-text) 8%, transparent);
    border-radius: 4px;
    animation: skel-pulse 1.4s ease-in-out infinite;
  }
  @keyframes skel-pulse {
    50% {
      opacity: 0.45;
    }
  }
  .skel-avatar {
    width: 36px;
    height: 36px;
    flex: none;
  }
  .skel-avatar.circle {
    border-radius: 50%;
  }
  .skel-avatar.square {
    border-radius: 8px;
  }
  .skel-line {
    height: 10px;
    margin: 3px 0;
  }
  .skel-line.thin {
    height: 8px;
  }
  .skel-line.centered {
    margin: 2px auto 0;
  }
  .skel-poster {
    width: 64px;
    height: 96px;
  }
  @media (prefers-reduced-motion: reduce) {
    .skel {
      animation: none;
    }
  }
  /* "If you like it" shelf per 27a: native momentum scroll, no arrows — the
     edge fade plus the peeking last poster are the "there's more" affordance */
  .shelf-head {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 8px;
  }
  .shelf-src {
    font-size: 9.5px;
    opacity: 0.7;
  }
  /* the strip clips at its scrollport, so the 3px the hover outline needs live
     as padding inside it; the negative margin keeps the tiles flush with the rail */
  .shelf {
    position: relative;
    margin: 0 -3px;
  }
  .shelf-strip {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 3px;
    scroll-snap-type: x proximity;
    /* the snapport must inset with the padding, or snap parks the strip at
       scrollLeft 3px — hiding the outline room and bouncing off scrollLeft 0 */
    scroll-padding-inline: 3px;
    scrollbar-width: none;
  }
  .shelf-strip::-webkit-scrollbar {
    display: none;
  }
  .shelf-tile {
    flex: none;
    width: 64px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    scroll-snap-align: start;
    cursor: pointer;
  }
  .shelf-poster-wrap {
    position: relative;
    display: block;
  }
  .shelf-poster {
    display: block;
    width: 64px;
    height: 96px;
    object-fit: cover;
    border-radius: 4px;
  }
  .shelf-tile:hover .shelf-poster {
    outline: 2px solid var(--color-accent);
    outline-offset: 1px;
  }
  .shelf-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--color-accent);
    color: var(--color-bg);
    font-size: 10px;
    font-weight: 600;
    display: grid;
    place-items: center;
  }
  .shelf-label {
    font-size: 10px;
    line-height: 1.3;
    text-align: center;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .shelf-year {
    font-size: 9.5px;
    text-align: center;
    opacity: 0.75;
    margin-top: -2px;
  }
  .shelf-fade {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 48px;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }
  .shelf-fade.right {
    right: 0;
    background: linear-gradient(90deg, transparent, var(--color-bg));
  }
  .shelf-fade.left {
    left: 0;
    background: linear-gradient(270deg, transparent, var(--color-bg));
  }
  .shelf-fade.hidden {
    opacity: 0;
  }
  /* centered on the poster row: 3px strip padding + half the 96px poster, minus half the button */
  .shelf-nav {
    position: absolute;
    top: 39px;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 1px solid var(--color-divider);
    border-radius: 50%;
    background: var(--color-surface);
    color: var(--color-text);
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    display: none;
    align-items: center;
    justify-content: center;
  }
  .shelf-nav.left {
    left: 0;
  }
  .shelf-nav.right {
    right: 0;
  }
  .shelf-nav:hover {
    border-color: var(--color-accent);
    color: var(--color-accent);
  }
  /* only mouse-driven devices need them; touch scrolls the strip directly */
  @media (hover: hover) and (pointer: fine) {
    .shelf-nav {
      display: flex;
    }
  }
  .remove-btn {
    font-size: 12px;
    justify-content: flex-start;
  }
  .remove-btn:first-of-type {
    margin-top: 4px;
  }
</style>
